import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { Filter, ChevronDown, User, Users, AlertTriangle, Clock, Loader2, List, Workflow, Layers, GitBranch, Play, AlertCircle, Calendar, ArrowUpDown, CheckCircle2, ListTodo } from 'lucide-react'
import React, { useState, useMemo, useCallback, useEffect } from 'react'

import {
  EmptyState,
  NoResultsEmptyState,
  determineEmptyStateType,
} from '@/components/common/empty-state'
import { InlineStatusSelect } from '@/components/common/inline-status-select'
import { QuickActionBar } from '@/components/pm/issue/quick-action-bar'
import { TaskPreviewTooltip, type TaskPreviewData } from '@/components/pm/issue/task-preview-tooltip'
import { TimelogPromptDialog, useTimelogPrompt } from '@/components/pm/timelog/timelog-prompt-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import {
  type IssueStatus, type IssuePriority, useUpdateIssueStatus,
  useMyTasks,
  type MyTasksQuery,
  type MyTaskItem,
  useEnhancedTasks,
  useGroupedTasks,
  GROUP_BY_OPTIONS,
  SORT_BY_OPTIONS,
  QUICK_FILTER_OPTIONS,
  formatDeadlineCountdown,
  formatWaitingTime,
  type EnhancedTasksParams,
  type GroupByOption,
  type SortByOption,
  type EnhancedTaskItem
} from '@/hooks'
import { useInfiniteTasks, calculateLoadProgress , useProjects } from '@/hooks'
import { getItem, setItem } from '@/lib/storage'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/pm/my-tasks')({
  component: MyTasksPage,
})

// 视图模式类型
type ViewMode = 'list' | 'workflow'

// 视图状态持久化 key
const VIEW_STATE_KEY = 'my-tasks-view-state'

interface ViewState {
  viewMode: ViewMode
  filters: MyTasksQuery
  workflowParams?: EnhancedTasksParams
}

const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: '待办',
  todo: '计划中',
  in_progress: '进行中',
  code_review: '代码审查',
  testing: '测试中',
  done: '已完成',
  blocked: '已阻塞',
  cancelled: '已取消',
}

const PRIORITY_LABELS: Record<IssuePriority, string> = {
  urgent: '紧急',
  high: '高',
  medium: '中',
  low: '低',
}

const PRIORITY_COLORS: Record<IssuePriority, string> = {
  urgent: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-gray-600',
}

function MyTasksPage() {
  // 从 localStorage 恢复视图状态 (Requirement 1.2)
  const savedState = useMemo(() => getItem<ViewState | null>(VIEW_STATE_KEY, null), [])

  const [viewMode, setViewMode] = useState<ViewMode>(savedState?.viewMode ?? 'list')
  const [filters, setFilters] = useState<MyTasksQuery>(savedState?.filters ?? {
    role: 'all',
    page: 1,
    pageSize: 20,
  })
  const [workflowParams, setWorkflowParams] = useState<EnhancedTasksParams>(savedState?.workflowParams ?? {
    groupBy: 'project',
    sortBy: 'deadline',
    sortOrder: 'asc',
    filter: 'all',
    page: 1,
    pageSize: 20,
  })
  const [showFilters, setShowFilters] = useState(false)
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [useInfiniteScroll] = useState(true)

  const navigate = useNavigate()

  // 保存视图状态到 localStorage (Requirement 1.2)
  useEffect(() => {
    const state: ViewState = { viewMode, filters, workflowParams }
    setItem(VIEW_STATE_KEY, state)
  }, [viewMode, filters, workflowParams])

  // 传统分页查询（当禁用无限滚动时使用）
  const { data: tasksResponse, isLoading: isLoadingPaginated, error: paginatedError } = useMyTasks(filters)

  // 无限滚动查询 (Requirement 6.1)
  const infiniteParams = useMemo(() => ({
    role: filters.role,
    projectId: filters.projectId,
    status: filters.status?.[0],
    priority: filters.priority?.[0],
  }), [filters.role, filters.projectId, filters.status, filters.priority])

  const {
    data: infiniteTasks,
    isLoading: isLoadingInfinite,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: infiniteError,
    total: infiniteTotal,
    isComplete,
    scrollRef,
  } = useInfiniteTasks<MyTaskItem>({
    queryKey: ['my-tasks', 'infinite'],
    endpoint: '/issues/my-tasks',
    params: infiniteParams,
    pageSize: 20,
    enabled: useInfiniteScroll && viewMode === 'list',
  })

  const { data: projects } = useProjects()
  const updateStatusMutation = useUpdateIssueStatus()
  const toast = useToast()
  const { promptState, showPrompt, setPromptOpen } = useTimelogPrompt()

  // 根据模式选择数据源
  const tasks = useMemo(
    () => (useInfiniteScroll ? infiniteTasks : (tasksResponse?.data ?? [])),
    [useInfiniteScroll, infiniteTasks, tasksResponse?.data]
  )
  const summary = tasksResponse?.summary
  const meta = tasksResponse?.meta
  const isLoading = useInfiniteScroll ? isLoadingInfinite : isLoadingPaginated
  const error = useInfiniteScroll ? infiniteError : paginatedError

  /**
   * 判断任务是否逾期
   * Property 2: Overdue Task Identification
   * Requirements: 5.1, 5.2
   */
  const isOverdue = (dueDate: string | null, status: string) => {
    if (!dueDate) { return false }
    // 已完成或已取消的任务不算逾期
    if (status === 'done' || status === 'cancelled') { return false }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const due = new Date(dueDate)
    due.setHours(0, 0, 0, 0)
    return due < today
  }

  // 计算逾期任务数量 (Requirement 5.4)
  const overdueCount = useMemo(() => {
    return tasks.filter((t) => isOverdue(t.dueDate, t.status)).length
  }, [tasks])

  // 过滤后的任务列表 (Requirement 5.3)
  const filteredTasks = useMemo(() => {
    if (!overdueOnly) { return tasks }
    return tasks.filter((t) => isOverdue(t.dueDate, t.status))
  }, [tasks, overdueOnly])

  // 处理状态更新
  const handleStatusChange = async (taskId: string, newStatus: IssueStatus) => {
    await updateStatusMutation.mutateAsync({
      id: taskId,
      data: { status: newStatus },
    })
  }

  // 处理工时记录点击 (Requirement 3.2)
  const handleTimelogClick = useCallback((task: MyTaskItem) => {
    showPrompt({
      id: task.id,
      title: task.title,
      code: task.code,
    })
  }, [showPrompt])

  // 处理评论点击 (Requirement 3.3)
  const handleCommentClick = useCallback((task: MyTaskItem) => {
    // 导航到任务详情页的评论区
    void navigate({
      to: '/pm/issues/$issueId',
      params: { issueId: task.id },
      hash: 'comments',
    })
  }, [navigate])

  // 转换任务为预览数据 (Requirement 4.2)
  // 注意：MyTaskItem 可能没有 description, assignees, recentComments 字段
  // 这里提供默认值以保证类型安全
  const getTaskPreviewData = useCallback((task: MyTaskItem): TaskPreviewData => ({
    id: task.id,
    title: task.title,
    description: null, // MyTaskItem 不包含 description
    assignees: task.owner ? [{ id: task.owner.id, name: task.owner.name }] : [],
    dueDate: task.dueDate,
    recentComments: [], // MyTaskItem 不包含 recentComments
  }), [])

  // 计算加载进度 (Requirement 6.3)
  const loadProgress = useMemo(() => {
    if (!useInfiniteScroll) { return null }
    return calculateLoadProgress(tasks.length, infiniteTotal)
  }, [useInfiniteScroll, tasks.length, infiniteTotal])

  // 视图切换处理 (Requirement 1.2 - 保持筛选状态)
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode)
    // 筛选状态保持不变
  }, [])

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 (Requirement 5.4 - 逾期徽章) */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">我的任务</h1>
            {overdueCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5 animate-pulse" />
                {overdueCount} 逾期
              </span>
            )}
          </div>
          <p className="text-muted-foreground">查看我负责或参与的所有任务</p>
        </div>
        {/* 视图切换按钮 (Requirement 1.1 - 统一入口) */}
        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-1">
          <button
            onClick={() => handleViewModeChange('list')}
            className={cn(
              'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              viewMode === 'list'
                ? 'bg-background shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <List className="h-4 w-4" />
            列表视图
          </button>
          <button
            onClick={() => handleViewModeChange('workflow')}
            className={cn(
              'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              viewMode === 'workflow'
                ? 'bg-background shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Workflow className="h-4 w-4" />
            工作流视图
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={() => setFilters((prev) => ({ ...prev, role: 'all', page: 1 }))}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
              filters.role === 'all' && 'border-primary bg-primary/5'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              全部任务
            </div>
            <div className="mt-2 text-2xl font-bold">{summary.total}</div>
          </button>

          <button
            onClick={() => setFilters((prev) => ({ ...prev, role: 'owner', page: 1 }))}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
              filters.role === 'owner' && 'border-primary bg-primary/5'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              我负责的
            </div>
            <div className="mt-2 text-2xl font-bold">{summary.asOwner}</div>
          </button>

          <button
            onClick={() => setFilters((prev) => ({ ...prev, role: 'participant', page: 1 }))}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
              filters.role === 'participant' && 'border-primary bg-primary/5'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              我参与的
            </div>
            <div className="mt-2 text-2xl font-bold">{summary.asParticipant}</div>
          </button>

          <button
            onClick={() => {
              setOverdueOnly(!overdueOnly)
            }}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
              overdueOnly && 'border-red-500 bg-red-50 dark:bg-red-900/20'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className={cn('h-4 w-4', overdueOnly ? 'text-red-500 animate-pulse' : 'text-red-500')} />
              已逾期
            </div>
            <div className="mt-2 text-2xl font-bold text-red-600">
              {overdueCount}
            </div>
            {overdueOnly && (
              <div className="mt-1 text-xs text-red-600">点击取消筛选</div>
            )}
          </button>
        </div>
      )}

      {/* 筛选 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showFilters ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Filter className="h-4 w-4" />
            筛选
            <ChevronDown className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')} />
          </button>
        </div>

        {showFilters && (
          <div className="rounded-lg border bg-card p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium">项目</label>
                <Select
                  value={filters.projectId || 'all'}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, projectId: value === 'all' ? undefined : value, page: 1 }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部项目" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部项目</SelectItem>
                    {projects?.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">状态</label>
                <Select
                  value={filters.status?.[0] || 'all'}
                  onValueChange={(value) => setFilters((prev) => ({
                    ...prev,
                    status: value === 'all' ? undefined : [value as IssueStatus],
                    page: 1,
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部状态" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部状态</SelectItem>
                    {(Object.keys(STATUS_LABELS) as IssueStatus[]).map((status) => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">优先级</label>
                <Select
                  value={filters.priority?.[0] || 'all'}
                  onValueChange={(value) => setFilters((prev) => ({
                    ...prev,
                    priority: value === 'all' ? undefined : [value as IssuePriority],
                    page: 1,
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部优先级" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部优先级</SelectItem>
                    {(Object.keys(PRIORITY_LABELS) as IssuePriority[]).map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {PRIORITY_LABELS[priority]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 任务列表 - 根据视图模式显示 */}
      {viewMode === 'list' ? (
        // 列表视图
        <>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="text-muted-foreground">加载中...</div>
            </div>
          ) : filteredTasks.length === 0 ? (
            // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
            (() => {
              const hasFilters = Boolean(filters.projectId || filters.status?.length || filters.priority?.length)
              const emptyStateType = determineEmptyStateType({
                hasData: false,
                hasFilters: hasFilters || overdueOnly,
                hasError: false,
                isFirstVisit: !summary || summary.total === 0,
              })

              if (overdueOnly) {
                return (
                  <EmptyState
                    type="no-results"
                    title="没有逾期任务"
                    description="太棒了！所有任务都在按时进行"
                    icon={CheckCircle2}
                    primaryAction={{
                      label: '查看全部任务',
                      onClick: () => setOverdueOnly(false),
                      variant: 'outline',
                    }}
                  />
                )
              }

              if (emptyStateType === 'no-results') {
                return (
                  <NoResultsEmptyState
                    title="未找到匹配的任务"
                    description="尝试调整筛选条件查看更多任务"
                    onClearFilters={() => {
                      setFilters({ role: 'all', page: 1, pageSize: 20 })
                    }}
                  />
                )
              }

              return (
                <EmptyState
                  type={emptyStateType}
                  title="暂无任务"
                  description="当前没有分配给你的任务"
                  icon={ListTodo}
                />
              )
            })()
          ) : (
            <>
              {/* 无限滚动容器 (Requirement 6.1) */}
              <div
                ref={useInfiniteScroll ? scrollRef : undefined}
                className={cn(
                  'rounded-lg border',
                  useInfiniteScroll && 'max-h-[600px] overflow-y-auto'
                )}
              >
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-background">
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-sm font-medium">角色</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">编号</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">标题</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">项目</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">优先级</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">截止日期</th>
                      <th className="px-4 py-3 text-left text-sm font-medium w-24">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map((task) => {
                      const taskIsOverdue = isOverdue(task.dueDate, task.status)
                      return (
                        <tr
                          key={task.id}
                          className={cn(
                            'group border-b last:border-0 transition-colors',
                            taskIsOverdue
                              ? 'bg-red-50/70 hover:bg-red-100/70 dark:bg-red-900/20 dark:hover:bg-red-900/30'
                              : 'hover:bg-muted/30'
                          )}
                        >
                          <td className="px-4 py-3">
                            {task.myRole === 'owner' ? (
                              <span className="inline-flex items-center gap-1 text-sm">
                                <User className="h-4 w-4 text-primary" />
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-sm">
                                <Users className="h-4 w-4 text-muted-foreground" />
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono">{task.code}</td>
                          <td className="px-4 py-3">
                            <TaskPreviewTooltip task={getTaskPreviewData(task)}>
                              <span className={cn(
                                'font-medium hover:text-primary cursor-pointer',
                                taskIsOverdue && 'text-red-700 dark:text-red-400'
                              )}>
                                {task.title}
                              </span>
                            </TaskPreviewTooltip>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{task.projectName}</td>
                          <td className="px-4 py-3">
                            <InlineStatusSelect
                              value={task.status}
                              onChange={(newStatus) => handleStatusChange(task.id, newStatus)}
                              onSaveSuccess={() => {
                                toast.success('状态已更新')
                              }}
                              onSaveError={(err) => {
                                toast.error('更新失败', err.message)
                              }}
                              onStatusDone={() => {
                                showPrompt({
                                  id: task.id,
                                  title: task.title,
                                  code: task.code,
                                })
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn('text-sm font-medium', PRIORITY_COLORS[task.priority])}>
                              {PRIORITY_LABELS[task.priority]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {task.dueDate ? (
                              <span className={cn(
                                'inline-flex items-center gap-1',
                                taskIsOverdue && 'text-red-600 dark:text-red-400 font-medium'
                              )}>
                                {taskIsOverdue && (
                                  <Clock className="h-4 w-4 animate-pulse" />
                                )}
                                {new Date(task.dueDate).toLocaleDateString('zh-CN')}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <QuickActionBar
                              onTimelogClick={() => handleTimelogClick(task)}
                              onCommentClick={() => handleCommentClick(task)}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {useInfiniteScroll && (
                  <div className="border-t p-4">
                    {isFetchingNextPage ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        加载更多...
                      </div>
                    ) : hasNextPage ? (
                      <div className="flex items-center justify-center">
                        <button
                          onClick={fetchNextPage}
                          className="text-sm text-primary hover:underline"
                        >
                          加载更多
                        </button>
                      </div>
                    ) : isComplete && tasks.length > 0 ? (
                      <div className="text-center text-sm text-muted-foreground">
                        已加载全部 {infiniteTotal} 条任务
                      </div>
                    ) : null}

                    {loadProgress && tasks.length > 0 && !isComplete && (
                      <div className="mt-2 text-center text-xs text-muted-foreground">
                        已加载 {tasks.length} / {infiniteTotal} ({loadProgress.percentage}%)
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!useInfiniteScroll && meta && meta.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    显示 {(meta.page - 1) * meta.pageSize + 1}-{Math.min(meta.page * meta.pageSize, meta.total)} / 共 {meta.total} 条
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                      disabled={meta.page <= 1}
                      className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    >
                      上一页
                    </button>
                    <span className="text-sm">{meta.page} / {meta.totalPages}</span>
                    <button
                      onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                      disabled={meta.page >= meta.totalPages}
                      className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    >
                      下一页
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        // 工作流视图
        <WorkflowView params={workflowParams} setParams={setWorkflowParams} />
      )}

      {/* 工时记录提示对话框 */}
      <TimelogPromptDialog
        open={promptState.open}
        onOpenChange={setPromptOpen}
        issueId={promptState.issueId}
        issueTitle={promptState.issueTitle}
        issueCode={promptState.issueCode}
      />
    </div>
  )
}

/**
 * 工作流视图组件 (Requirement 1.1 - 整合工作流视图)
 */
function WorkflowView({
  params,
  setParams,
}: {
  params: EnhancedTasksParams
  setParams: React.Dispatch<React.SetStateAction<EnhancedTasksParams>>
}) {
  const { data: groupedData, isLoading, error } = useGroupedTasks(params)
  const { data: listData } = useEnhancedTasks(params)

  const groups = groupedData ?? []
  const allTasks = listData?.data ?? []
  const totalTasks = groups.reduce((sum, g) => sum + g.totalCount, 0)
  const overdueCount = groups.reduce((sum, g) => sum + g.overdueCount, 0)
  const requiresActionCount = allTasks.filter((t) => t.requiresAction).length

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 快捷筛选 */}
      <div className="flex flex-wrap items-center gap-2">
        {QUICK_FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setParams((prev) => ({ ...prev, filter: option.value, page: 1 }))}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              params.filter === option.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80',
              option.color && params.filter !== option.value && option.color
            )}
          >
            {option.value === 'overdue' && <AlertCircle className="h-3.5 w-3.5" />}
            {option.value === 'dueToday' && <Calendar className="h-3.5 w-3.5" />}
            {option.value === 'dueThisWeek' && <Clock className="h-3.5 w-3.5" />}
            {option.value === 'atRisk' && <AlertTriangle className="h-3.5 w-3.5" />}
            {option.label}
            {option.value === 'overdue' && overdueCount > 0 && (
              <span className="ml-1 rounded-full bg-red-100 px-1.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {overdueCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 统计卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Layers className="h-4 w-4" />
            全部任务
          </div>
          <div className="mt-2 text-2xl font-bold">{totalTasks}</div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4 text-red-500" />
            已超时
          </div>
          <div className="mt-2 text-2xl font-bold text-red-600">{overdueCount}</div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            需要操作
          </div>
          <div className="mt-2 text-2xl font-bold text-amber-600">{requiresActionCount}</div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Layers className="h-4 w-4" />
            分组数
          </div>
          <div className="mt-2 text-2xl font-bold">{groups.length}</div>
        </div>
      </div>

      {/* 分组和排序选项 */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <Select
            value={params.groupBy || 'project'}
            onValueChange={(value) =>
              setParams((prev) => ({ ...prev, groupBy: value as GroupByOption, page: 1 }))
            }
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="分组方式" />
            </SelectTrigger>
            <SelectContent>
              {GROUP_BY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <Select
            value={params.sortBy || 'deadline'}
            onValueChange={(value) =>
              setParams((prev) => ({ ...prev, sortBy: value as SortByOption, page: 1 }))
            }
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="排序方式" />
            </SelectTrigger>
            <SelectContent>
              {SORT_BY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() =>
              setParams((prev) => ({
                ...prev,
                sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
              }))
            }
            className="rounded-md border p-2 hover:bg-muted"
            title={params.sortOrder === 'asc' ? '升序' : '降序'}
          >
            <ArrowUpDown
              className={cn(
                'h-4 w-4 transition-transform',
                params.sortOrder === 'desc' && 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>

      {/* 任务分组列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : groups.length === 0 ? (
        // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
        (() => {
          const hasFilters = params.filter !== 'all'
          const emptyStateType = determineEmptyStateType({
            hasData: false,
            hasFilters,
            hasError: false,
            isFirstVisit: totalTasks === 0,
          })

          if (emptyStateType === 'no-results') {
            return (
              <NoResultsEmptyState
                title="未找到匹配的任务"
                description="尝试调整筛选条件查看更多任务"
                onClearFilters={() => {
                  setParams((prev) => ({ ...prev, filter: 'all', page: 1 }))
                }}
              />
            )
          }

          return (
            <EmptyState
              type={emptyStateType}
              title="暂无工作流任务"
              description="当前没有需要你处理的工作流任务"
              icon={Workflow}
            />
          )
        })()
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.groupKey} className="rounded-lg border">
              <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
                {params.groupBy === 'project' && <Layers className="h-5 w-5 text-blue-500" />}
                {params.groupBy === 'stage' && <Play className="h-5 w-5 text-green-500" />}
                {params.groupBy === 'track' && <GitBranch className="h-5 w-5 text-purple-500" />}
                <h3 className="font-medium">{group.groupName}</h3>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{group.totalCount}</span>
                {group.overdueCount > 0 && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    {group.overdueCount} 超时
                  </span>
                )}
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-4 py-2 text-left text-sm font-medium">角色</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">任务</th>
                    {params.groupBy !== 'project' && (
                      <th className="px-4 py-2 text-left text-sm font-medium">项目</th>
                    )}
                    {params.groupBy !== 'stage' && (
                      <th className="px-4 py-2 text-left text-sm font-medium">阶段</th>
                    )}
                    <th className="px-4 py-2 text-left text-sm font-medium">截止日期</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">等待时间</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">状态</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {group.tasks.map((task) => (
                    <WorkflowTaskRow key={task.issueId} task={task} groupBy={params.groupBy || 'project'} />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * 工作流任务行组件
 */
function WorkflowTaskRow({
  task,
  groupBy,
}: {
  task: EnhancedTaskItem
  groupBy: GroupByOption
}) {
  const getTimeStatusColor = (status: string) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      case 'approaching_deadline':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      default:
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600'
      case 'high':
        return 'text-orange-600'
      case 'medium':
        return 'text-blue-600'
      default:
        return 'text-gray-600'
    }
  }

  return (
    <tr
      className={cn(
        'border-b last:border-0 hover:bg-muted/30',
        task.requiresAction && 'bg-amber-50/50 dark:bg-amber-900/10'
      )}
    >
      <td className="px-4 py-3">
        {task.role === 'owner' ? (
          <span className="inline-flex items-center gap-1 text-sm" title="负责人">
            <User className="h-4 w-4 text-primary" />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-sm" title="参与人员">
            <Users className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to="/pm/issues/$issueId"
          params={{ issueId: task.issueId }}
          className="font-medium hover:text-primary hover:underline"
        >
          {task.issueTitle}
        </Link>
        <span className={cn('ml-2 text-xs', getPriorityColor(task.priority))}>
          [{task.priority === 'urgent' ? '紧急' : task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}]
        </span>
      </td>
      {groupBy !== 'project' && (
        <td className="px-4 py-3 text-sm text-muted-foreground">{task.projectName}</td>
      )}
      {groupBy !== 'stage' && (
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            <Play className="h-3 w-3" />
            {task.currentStage}
          </span>
        </td>
      )}
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-flex items-center gap-1 text-sm',
            task.timeStatus === 'overdue' && 'text-red-600',
            task.timeStatus === 'approaching_deadline' && 'text-amber-600'
          )}
        >
          <Calendar className="h-3.5 w-3.5" />
          {formatDeadlineCountdown(task.deadlineCountdown)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {formatWaitingTime(task.waitingTime)}
        </span>
      </td>
      <td className="px-4 py-3">
        {task.requiresAction ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <AlertCircle className="h-3 w-3" />
            需要操作
          </span>
        ) : (
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', getTimeStatusColor(task.timeStatus))}>
            {task.timeStatus === 'overdue' ? '已超时' : task.timeStatus === 'approaching_deadline' ? '即将到期' : '正常'}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to="/pm/issues/$issueId"
          params={{ issueId: task.issueId }}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          查看详情
        </Link>
      </td>
    </tr>
  )
}
